import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TileList } from '@as/cotopaxi';
import { determineSelectedSku } from '@as-react/common-utils';
import { BottomSheet, Box, ButtonAlpha, Sidebar, TextAlpha, useIsClient, useLockBodyScroll, useMediaQueryMatchesOnDesktopLarge, useMediaQueryMatchesOnMobile, } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { productActions } from '../../redux/product/actions/ProductActions';
import { makeGetSelectedBundleAlternativeTab } from '../../redux/product/selectors/ProductSelector';
import { ProductBundleAlternativeItem } from './ProductBundleAlternativeItem';
const ProductBundleAlternativeOverlay = ({ children, footerElement, isMobile, onClose, title, }) => {
    useLockBodyScroll(true);
    const isDesktopLarge = useMediaQueryMatchesOnDesktopLarge();
    const sidebarWidth = isDesktopLarge ? '480px' : '420px';
    const content = (_jsxs(Box, { marginInline: { base: '-5', md: '-6', lg: '-8' }, children: [_jsx(Box, { paddingInline: { base: '4', md: '8' }, paddingBlockEnd: "5", children: _jsx(TextAlpha, { as: "h2", color: "emphasised", variant: "headingS", children: title }) }), children] }));
    return createPortal(isMobile ? (_jsx(BottomSheet, { ariaLabel: title, footer: footerElement, fullHeightOnScroll: true, hasCloseBtn: true, isOpen: true, onClose: onClose, padding: "none", testId: "product-bundle-alternative-bottom-sheet", zIndex: "sticky", children: content })) : (_jsx(Sidebar, { ariaLabel: title, customWidth: sidebarWidth, footer: { content: footerElement, position: 'absolute' }, hasBackdrop: true, hasCloseBtn: true, hasCloseBtnSpacing: false, isOpen: true, onClose: onClose, position: "fixed", side: "right", testId: "product-bundle-alternative-sidebar", zIndex: "sticky", children: content })), document.body);
};
export const ProductBundleAlternative = ({ productSlot, onSubmit, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const dispatch = useDispatch();
    const isClient = useIsClient();
    const isMobile = useMediaQueryMatchesOnMobile();
    const selectedBundleAlternativeTab = useSelector(makeGetSelectedBundleAlternativeTab());
    const { bundleChildren: alternatives, displayedProduct, slotNumber } = productSlot;
    const isOpen = selectedBundleAlternativeTab === `bundle-alternatives-slot-${slotNumber}`;
    const [selectedAlternative, setSelectedAlternative] = useState([alternatives[0].productCode]);
    const [formState, setFormState] = useState({ submitCount: 0 });
    const [productState, setProductState] = useState(() => alternatives.reduce((prev, curr) => {
        const currColour = curr.colours[0];
        return Object.assign(Object.assign({}, prev), { [curr.productCode]: {
                productCode: curr.productCode,
                productId: curr.productId,
                colourId: curr.colourId || currColour.colourId,
                skuId: determineSelectedSku(currColour),
            } });
    }, {}));
    const resetSelection = () => {
        setFormState({ submitCount: 0 });
        if (!displayedProduct) {
            setSelectedAlternative([alternatives[0].productCode]);
            return;
        }
        setSelectedAlternative([displayedProduct.productCode]);
    };
    const handleClose = () => {
        dispatch(productActions.bundleSetSelectedAlternativeTab(null));
    };
    useEffect(() => {
        resetSelection();
        if (displayedProduct) {
            setProductState(prevState => (Object.assign(Object.assign({}, prevState), { [displayedProduct.productCode]: Object.assign({}, displayedProduct) })));
        }
    }, [displayedProduct]);
    const closePopoverAndDispatchChosenProduct = (product) => {
        if (product.colourId && product.skuId) {
            handleClose();
            onSubmit(product);
        }
        else {
            setFormState(prev => ({ submitCount: prev.submitCount + 1, error: selectedAlternative[0] }));
        }
    };
    const handlePopoverSubmit = () => closePopoverAndDispatchChosenProduct(productState[selectedAlternative[0]]);
    const handleCloseAndReset = () => {
        handleClose();
        resetSelection();
    };
    const handleProductChange = (productCode, product, shouldClosePopup = false) => {
        setSelectedAlternative([productCode]);
        if (product) {
            setProductState(prevState => (Object.assign(Object.assign({}, prevState), { [productCode]: product })));
        }
        if (shouldClosePopup) {
            closePopoverAndDispatchChosenProduct(Object.assign({ productCode }, product));
        }
    };
    if (!isClient || !isOpen)
        return null;
    return (_jsx(ProductBundleAlternativeOverlay, { footerElement: _jsx(Box, { paddingInline: { lg: '4' }, children: _jsx(ButtonAlpha, { fullWidth: true, theme: "branded", text: t('product.bundleDeals.alternative.cta.save'), onClick: handlePopoverSubmit }) }), isMobile: isMobile, onClose: handleCloseAndReset, title: t('product.bundleDeals.alternative.title_plural'), children: _jsx(TileList, { tileListName: "alternatives", selectable: true, selectableType: "radio", selectedItems: selectedAlternative, onSelectionChange: setSelectedAlternative, boxProps: {
                mobile: { spacing: { marginLeft: 2, marginRight: 2 } },
                tablet: { spacing: { marginLeft: 4, marginRight: 4 } },
            }, children: alternatives.map((alternative, index) => (_jsx(ProductBundleAlternativeItem, { product: alternative, displayed: productState === null || productState === void 0 ? void 0 : productState[alternative.productCode], formState: formState, onProductChange: handleProductChange }, index))) }) }));
};
